import PropTypes from 'prop-types'

import {Map} from 'immutable'
import {useState, useEffect} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {connect} from 'react-redux'
import {createResource, readEndpoint, updateResource} from 'redux-json-api'

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText
} from '@mui/material'

import Loader from '../../../../shared_components/NewLoader'
import PageTitle from '../../../../shared_components/PageTitle'
import Button from '../../../../shared_components/mui_base_components/button/Button'
import ButtonGroup from '../../../../shared_components/mui_base_components/button/ButtonGroup'

import {currentUserConfig} from '../../../../lib/userTools'
import {cleanResponse} from '../corp_max/redux/apiHelper'
import {useNotificationContext} from '../../../../shared_components/notifications/NotificationContext'
import routerUtils from '../../../../lib/routerUtils'
import {accountSettingsUrl} from '../../../../lib/urlTools'

import './emailPreferences.scss'

export const EmailPreferences = props => {
  const [processing, setProcessing] = useState(false)
  const {alwaysNotify} = useNotificationContext()

  const {control, handleSubmit, formState: {isDirty}, reset} = useForm({
    defaultValues: {
      deputyDigest: true,
      monthlyNewsletter: true
    }
  })

  useEffect(() => {
    props.readEndpoint(`email-preferences/${props.userConfig.get('id')}`)
  }, [])

  useEffect(() => {
    if (typeof props.subscribedToNewsletter !== 'undefined' &&
      typeof props.sendsDeputyDigest !== 'undefined') {
      reset({
        deputyDigest: props.sendsDeputyDigest,
        monthlyNewsletter: props.subscribedToNewsletter
      })
    }
  }, [props.sendsDeputyDigest, props.subscribedToNewsletter, reset])

  const goToAccountSettings = () => { routerUtils.setLocation(accountSettingsUrl) }

  const postSubmit = () => ({
    success: () => {
      alwaysNotify.shortSuccess('Your preferences have been saved.')
    },
    error: () => {
      alwaysNotify.shortError(
        'Your preferences could not be saved. Please try again.'
      )
    }
  })

  const updateEmailPreferences = payload => {
    props.updateResource(payload)
      .then(() => {
        postSubmit().success()
      })
      .catch(() => {
        postSubmit().error()
      })
      .finally(() => {
        setProcessing(false)
        goToAccountSettings()
      })
  }

  const submit = formData => {
    setProcessing(true)

    const payload = {
      type: 'email-preferences',
      id: props.userConfig.get('id'),
      attributes: {
        'sends-deputy-digest': `${formData.deputyDigest}`,
        subscribe: `${formData.monthlyNewsletter}`
      }
    }

    updateEmailPreferences(payload)
  }

  if (props.loading) {
    return <Loader loading />
  } else {
    return (
      <div className='email-preferences'>
        <PageTitle title='Email Preferences' />
        <h3>Email Preferences</h3>
        <p className='subtitle'>
          Manage which emails you’d like to receive (or for your Deputies to
          receive). Uncheck any you’d like to opt out of.
        </p>
        <form
          className='email-preferences-settings'
          onSubmit={handleSubmit(submit)}>
          <FormGroup className='form-group'>
            <Controller
              name='deputyDigest'
              control={control}
              render={({field}) => (
                <Box>
                  <FormControlLabel
                    className='label'
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={event => field.onChange(event.target.checked)}
                      />
                    }
                    label='Deputy Digest'
                  />
                  <FormHelperText>
                    A weekly summary email to your Deputies when you update
                    sections of your Everplan they can view
                  </FormHelperText>
                </Box>
              )}
            />
            <Controller
              name='monthlyNewsletter'
              control={control}
              render={({field}) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={event => field.onChange(event.target.checked)}
                      />
                    }
                    label='Monthly Newsletter'
                  />
                  <FormHelperText>
                    A monthly newsletter with tips, insights, and best practices
                    for planning and organizing
                  </FormHelperText>
                </Box>
              )}
            />
          </FormGroup>
          <ButtonGroup className='button-group'>
            <Button
              color='cancel'
              disabled={processing}
              onClick={goToAccountSettings}>
              Cancel
            </Button>
            <Button disabled={!isDirty} loading={processing} type='submit'>
              Save
            </Button>
          </ButtonGroup>
        </form>
      </div>
    )
  }
}

EmailPreferences.propTypes = {
  createResource: PropTypes.func,
  readEndpoint: PropTypes.func,
  sendsDeputyDigest: PropTypes.bool,
  subscribedToNewsletter: PropTypes.bool,
  updateResource: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map)
}

const mapStateToProps = ({api}) => {
  const sendsDeputyDigest = cleanResponse(api['email-preferences']).getIn([
    0,
    'sends-deputy-digest'
  ])

  const subscribedToNewsletter = cleanResponse(api['email-preferences']).getIn([
    0,
    'subscribed-to-newsletter'
  ])

  return {
    loading: !api['email-preferences'],
    sendsDeputyDigest,
    subscribedToNewsletter,
    userConfig: currentUserConfig(api)
  }
}

export default connect(
  mapStateToProps, {createResource,
    readEndpoint,
    updateResource
  })(EmailPreferences)
